/**
 * QuestDashboard — list active + completed + available quests.
 */

import { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { api, type QuestSummary, type QuestsListResponse } from '../cloud/api';

export function QuestDashboard() {
    const [data, setData] = useState<QuestsListResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    async function load() {
        setLoading(true);
        try {
            const r = await api.questsList();
            setData(r);
            setError(null);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    async function start(id: string) {
        setBusyId(id);
        try {
            await api.questStart(id);
            await load();
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setBusyId(null);
        }
    }

    if (loading) return <p className="muted">Loading quests…</p>;
    if (error) return <p className="text-danger text-sm">Couldn't load quests — {error}.</p>;
    if (!data) return null;

    return (
        <div className="space-y-4">
            <div className="card">
                <div className="muted text-xs">your stats</div>
                <div className="flex gap-4 mt-1">
                    <div><span className="font-semibold">{data.glow}</span> <span className="muted">glow</span></div>
                    <div><span className="font-semibold">{data.marks_count}</span> <span className="muted">marks</span></div>
                </div>
            </div>

            <Section title="Active">
                {data.active.length === 0
                    ? <Empty>None active. Start one below.</Empty>
                    : data.active.map((q) => <ActiveCard key={q.id} q={q} />)}
            </Section>

            <Section title="Available">
                {data.available.length === 0
                    ? <Empty>No new quests right now.</Empty>
                    : data.available.map((q) => (
                        <AvailableCard key={q.id} q={q} busy={busyId === q.id} onStart={() => start(q.id)} />
                    ))}
            </Section>

            {data.completed.length > 0 && (
                <Section title="Completed">
                    {data.completed.map((q) => <CompletedCard key={q.id} q={q} />)}
                </Section>
            )}
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <h3 className="text-sm font-semibold mb-2">{title}</h3>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function Empty({ children }: { children: React.ReactNode }) {
    return <p className="muted text-sm card">{children}</p>;
}

function ActiveCard({ q }: { q: QuestSummary }) {
    const visits = q.progress?.visits ?? 0;
    const target = q.goal?.target_visits ?? 0;
    const pct = target > 0 ? Math.min(100, Math.round((visits / target) * 100)) : 0;
    return (
        <div className="card">
            <div className="flex justify-between">
                <h4 className="font-medium">{q.title}</h4>
                <span className="muted text-xs">{q.category}</span>
            </div>
            <p className="muted text-sm mt-1">{q.description}</p>
            <div className="mt-2">
                <div className="flex justify-between text-xs mb-0.5">
                    <span>{visits} of {target}</span>
                    <span>{pct}%</span>
                </div>
                <div className="h-1.5 bg-line rounded-full overflow-hidden">
                    <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                </div>
            </div>
            {(q.reward.glow || q.reward.marks) && (
                <p className="muted text-xs mt-2">
                    reward · {[
                        q.reward.glow ? `${q.reward.glow} glow` : null,
                        q.reward.marks ? `${q.reward.marks.length} mark` : null,
                    ].filter(Boolean).join(' · ')}
                </p>
            )}
        </div>
    );
}

function AvailableCard({ q, busy, onStart }: { q: QuestSummary; busy: boolean; onStart: () => void }) {
    return (
        <div className="card">
            <div className="flex justify-between items-start gap-2">
                <div>
                    <h4 className="font-medium">{q.title}</h4>
                    <p className="muted text-sm mt-1">{q.description}</p>
                </div>
                <button
                    className="btn-primary text-xs"
                    onClick={onStart}
                    disabled={busy}
                >{busy ? '…' : 'Start'}</button>
            </div>
        </div>
    );
}

function CompletedCard({ q }: { q: QuestSummary & { completed_at: number } }) {
    return (
        <div className="card opacity-70">
            <div className="flex justify-between">
                <h4 className="font-medium">{q.title}</h4>
                <span className="muted inline-flex items-center gap-1 text-xs">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden strokeWidth={2.2} />
                    <span>done</span>
                </span>
            </div>
        </div>
    );
}
